// Die Bereiche der Inhalte-Seite — EINE Komponente (`ContentManagement`), zwei Vorgaben.
//
// ⚠️ Vorgeschichte: `App.tsx` hängte `content/blog` UND `content/affirmations` an dieselbe
// Komponente, ungefiltert. Zwei Menüpunkte, eine Liste, identischer Inhalt — der Benutzer glaubte,
// zwei getrennte Bereiche zu haben. Der Sanierungsplan hat das als Befund D notiert
// (`docs/plans/plan-cms-sanierung-2026-08.md`, Zeile 206) mit der Wahl „route-abhängig filtern
// oder den Menüpunkt streichen".
//
// **Gefiltert, nicht gestrichen** — begründet an der Messung, nicht am Geschmack:
//   - `AFFIRMATION` ist ein echter Wert des Prisma-Enums `ContentType` (in der Datenbank belegt:
//     `select enumlabel from pg_enum` liefert AUDIO/VIDEO/BLOG/SHOP/AFFIRMATION/MINDBOOK).
//   - Das Backend hat einen eigenen Lesepfad (`GET /api/content/affirmations` →
//     `ContentController.getAffirmationContent`) und einen Schreibpfad (`ContentValidation`
//     verlangt `contentData.affirmation.text` bei `type=AFFIRMATION`).
//   - Die öffentliche Web-App verbraucht ihn tatsächlich: `apps/premium-web/[locale]/affirmations`
//     samt `affirmationService.getAffirmations()` → genau dieser Endpunkt.
// Damit ist es NICHT der Fall von „Analytics/Einstellungen" (kein Typ, kein Backend, nichts) —
// dort war Streichen richtig, hier wäre es Datenverlust an der Oberfläche.
//
// Was dem CMS für Affirmationen fehlt, ist einzig ein Formular. Deshalb trägt der Bereich kein
// `editor`-Objekt, und die Seite zeigt dort weder „Anlegen" noch „Bearbeiten". Ein Knopf, der das
// Blog-Formular öffnete, würde eine Affirmation stillschweigend als `contentData.blog` speichern —
// dieselbe Attrappen-Klasse, nur teurer.
import type React from 'react';
import { DocumentTextIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { ContentEditorFlow, ContentType, type ContentFilters } from '../types';

/**
 * Was der Benutzer auf der Seite selbst einstellt. `type` gehört bewusst NICHT dazu: der Typ ist
 * die Identität der Seite, keine Auswahl. Stünde er im Zustand, könnte die Blog-Seite wieder
 * Affirmationen zeigen — genau der Zustand, den dieser Umbau beseitigt.
 */
export type BereichsFilter = Omit<ContentFilters, 'type'>;

/** Formular-Angaben eines Bereichs. Fehlen genau dann, wenn das CMS für den Typ keines hat. */
export interface BereichsEditor {
  readonly flow: ContentEditorFlow;
  /** Beschriftung des Knopfs in der Titelzeile. */
  readonly anlegenKnopf: string;
  readonly titelNeu: string;
  readonly titelBearbeiten: string;
  readonly untertitelNeu: string;
  readonly untertitelBearbeiten: string;
}

export interface ContentBereich {
  /** Inhaltstyp dieser Seite — fest. Wandert bei jeder Abfrage in den Server-Filter. */
  readonly type: ContentType;
  readonly titel: string;
  readonly untertitel: string;
  readonly Icon: React.ComponentType<{ className?: string }>;
  /** Farbklasse des Symbols — hält die bisherige Farbgebung der Typ-Symbole fest. */
  readonly iconKlasse: string;
  readonly editor?: BereichsEditor;
  /** Überschrift der leeren Liste, wenn KEIN Filter gesetzt ist. */
  readonly leerTitel: string;
  /** Erklärtext dazu — sagt, woher Inhalte dieses Typs kommen. */
  readonly leerText: string;
}

export const BLOG_BEREICH: ContentBereich = {
  type: ContentType.BLOG,
  titel: 'Blog-Beiträge',
  untertitel: 'Beiträge für den Bereich „Aktuelles“',
  Icon: DocumentTextIcon,
  iconKlasse: 'text-success-600',
  editor: {
    flow: ContentEditorFlow.WEB_COMPONENT,
    anlegenKnopf: 'Beitrag anlegen',
    titelNeu: 'Neuen Blog-Beitrag erstellen',
    titelBearbeiten: 'Blog-Beitrag bearbeiten',
    untertitelNeu: 'Erstellen Sie einen neuen Beitrag für „Aktuelles“',
    untertitelBearbeiten: 'Bearbeiten Sie die Details des Beitrags',
  },
  leerTitel: 'Noch keine Blog-Beiträge',
  leerText: 'Legen Sie den ersten Beitrag an — er erscheint danach im Bereich „Aktuelles“.',
};

export const AFFIRMATIONEN_BEREICH: ContentBereich = {
  type: ContentType.AFFIRMATION,
  titel: 'Affirmationen',
  untertitel: 'Affirmationen für die öffentliche Web-App',
  Icon: SparklesIcon,
  iconKlasse: 'text-purple-600',
  // Kein `editor`: siehe Kopf dieser Datei.
  leerTitel: 'Noch keine Affirmationen',
  leerText:
    'Das CMS bringt für Affirmationen bisher kein Formular mit. Angelegt werden sie über die API '
    + '(POST /api/content mit type AFFIRMATION); vorhandene veröffentlicht, archiviert und löscht '
    + 'hier die Moderation.',
};

/** Leer-Text, wenn Suche oder Status gesetzt sind — dann liegt es am Filter, nicht am Bestand. */
export const LEER_MIT_FILTER = 'Keine Treffer. Passen Sie Suche oder Status an.';

/**
 * Baut die Abfrage-Filter für einen Bereich.
 *
 * Der Bereichs-Typ steht bewusst RECHTS vom Spread: er gewinnt gegen jedes `type`, das von außen
 * mitkäme. Serverseitig gefiltert wird deshalb, weil das Backend es kann — `getAllContent` reicht
 * `type` bis `buildContentWhere` durch (`where.type`). Alles zu laden und im Browser wegzuwerfen
 * wäre bei Seitengröße 10 schlicht falsch: die Blätter-Leiste zählte dann fremde Zeilen mit.
 *
 * ⚠️ Der Wert muss GROSS geschrieben sein. Am laufenden System gemessen: `?type=audio` → 400,
 * `?type=AUDIO` → 200. `ContentType` trägt die Prisma-Schreibweise bereits.
 */
export function bereichsFilter(bereich: ContentBereich, filter: ContentFilters): ContentFilters {
  return { ...filter, type: bereich.type };
}
